import React, { createContext, useContext, useMemo } from 'react';
import {
  canonicalizeLocaleTag,
  createMessageRuntime,
  type MessageRuntime,
} from '@bright-web-skeleton/shared';

const KoordinatorI18nContext = createContext<MessageRuntime | null>(null);

export const KOORDINATOR_DEFAULT_PLATFORM_LOCALE = 'en-US';
const KOORDINATOR_POLISH_PLATFORM_LOCALE = 'pl-PL';

/**
 * Resolve an explicit Koordinator platform UI locale.
 *
 * The application supplies this authority. BrandStore locale, browser language,
 * and storage are deliberately outside this resolver. Unsupported or invalid
 * values fail safely to canonical English.
 */
export function resolveKoordinatorPlatformLocale(
  platformLocale: string | null | undefined,
): string {
  const canonical = canonicalizeLocaleTag(platformLocale);
  const language = canonical?.split('-')[0]?.toLowerCase();

  if (language === 'pl') return KOORDINATOR_POLISH_PLATFORM_LOCALE;
  if (language === 'en') return KOORDINATOR_DEFAULT_PLATFORM_LOCALE;
  return KOORDINATOR_DEFAULT_PLATFORM_LOCALE;
}

export function createKoordinatorMessageRuntime(
  platformLocale: string | null | undefined,
): MessageRuntime {
  const locale = resolveKoordinatorPlatformLocale(platformLocale);
  return createMessageRuntime({
    locale,
    defaultLocale: KOORDINATOR_DEFAULT_PLATFORM_LOCALE,
    timeZone: 'UTC',
  });
}

export interface KoordinatorI18nProviderProps {
  children: React.ReactNode;
  /** Explicit platform UI locale. Defaults to canonical English. */
  platformLocale?: string | null;
}

/**
 * Bind Koordinator messages to explicit platform UI locale authority.
 *
 * Regional BrandStore locale remains separate and is supplied directly to
 * commerce-formatting surfaces such as each product card. This adapter never
 * reads BrandStore locale, browser language, storage, or the database.
 */
export const KoordinatorI18nProvider: React.FC<KoordinatorI18nProviderProps> = ({
  children,
  platformLocale = KOORDINATOR_DEFAULT_PLATFORM_LOCALE,
}) => {
  const runtime = useMemo(() => createKoordinatorMessageRuntime(platformLocale), [platformLocale]);

  return (
    <KoordinatorI18nContext.Provider value={runtime}>{children}</KoordinatorI18nContext.Provider>
  );
};

export function useKoordinatorI18n(): MessageRuntime {
  const runtime = useContext(KoordinatorI18nContext);
  if (!runtime) {
    throw new Error('useKoordinatorI18n must be used within KoordinatorI18nProvider');
  }
  return runtime;
}
